import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "../Utils/baseQueryWithReauth";
import { IBwCCustomersResponse, IBwCParticipantNotesResponse, IBwCFeatureRequestsResponse, CustomerStatusType, IBwCAIChatRequest, IBwCAIChatResponse } from "../components/BuildWithCustomer/BuildWithCustomer.interfaces";

/**
 * API slice for Build with Customer functionality
 * Provides endpoint to fetch BwC customer data from Kusto
 *
 * Caching Strategy:
 * - keepUnusedDataFor: 300 seconds (5 minutes) - data stays cached even when component unmounts
 * - refetchOnMountOrArgChange: false - prevents refetch on every tab switch
 * - refetchOnFocus: false - prevents refetch when window regains focus
 */
export const buildWithCustomerAPI = createApi({
  reducerPath: "buildWithCustomer",
  tagTypes: ["BwCCustomers", "BwCParticipantNotes", "BwCFeatureRequests"],
  baseQuery: baseQueryWithReauth,
  // Global cache settings - keep data for 5 minutes to avoid repeated API calls on tab switches
  keepUnusedDataFor: 300,
  refetchOnMountOrArgChange: false,
  refetchOnFocus: false,
  refetchOnReconnect: false,
  endpoints: (builder) => ({
    /**
     * Fetches all BwC customers from the CRM_DimProgram and CRM_DimProgramParticipants tables
     * Squad metrics (total squads, squads with/without customers) are included in the response
     * Returns customers list and Kusto query link for exploring data
     */
    getBwCCustomers: builder.query<IBwCCustomersResponse, void>({
      query: () => ({
        url: "bwc/customers",
        method: "GET"
      }),
      providesTags: ["BwCCustomers"],
      // Keep this data cached for 5 minutes
      keepUnusedDataFor: 300,
      transformResponse: (response: IBwCCustomersResponse) => {
        // Transform customer status: Show "Nominated" instead of "New"
        return {
          ...response,
          customers: response.customers.map((customer) => ({
            ...customer,
            customerStatus: (customer.customerStatus === "New" ? "Nominated" : customer.customerStatus) as CustomerStatusType
          }))
        };
      }
    }),

    /**
     * Fetches all notes for a BwC program participant
     * Returns notes list ordered by most recent modification date
     */
    getBwCParticipantNotes: builder.query<IBwCParticipantNotesResponse, { programParticipantId: string }>({
      query: ({ programParticipantId }) => ({
        url: `bwc/participant/${programParticipantId}/notes`,
        method: "GET"
      }),
      providesTags: (_result, _error, { programParticipantId }) => [
        { type: "BwCParticipantNotes", id: programParticipantId }
      ],
      keepUnusedDataFor: 60 // Cache notes for 1 minute
    }),

    /**
     * Fetches all feature requests and evidence for a BwC program participant
     * Returns feature requests list ordered by most recent creation date
     */
    getBwCParticipantFeatureRequests: builder.query<IBwCFeatureRequestsResponse, { programParticipantId: string }>({
      query: ({ programParticipantId }) => ({
        url: `bwc/participant/${programParticipantId}/feature-requests`,
        method: "GET"
      }),
      providesTags: (_result, _error, { programParticipantId }) => [
        { type: "BwCFeatureRequests", id: programParticipantId }
      ],
      keepUnusedDataFor: 60 // Cache feature requests for 1 minute
    }),

    /**
     * Fetches all notes for multiple BwC program participants in a single batch
     * Used for export functionality to avoid N+1 API calls
     */
    getBwCParticipantNotesBatch: builder.mutation<IBwCParticipantNotesResponse, { programParticipantIds: string[] }>({
      query: ({ programParticipantIds }) => ({
        url: "bwc/participants/notes/batch",
        method: "POST",
        body: { programParticipantIds }
      })
    }),

    /**
     * Fetches all feature requests and evidence for multiple BwC program participants in a single batch
     * Used for export functionality to avoid N+1 API calls
     */
    getBwCParticipantFeatureRequestsBatch: builder.mutation<IBwCFeatureRequestsResponse, { programParticipantIds: string[] }>({
      query: ({ programParticipantIds }) => ({
        url: "bwc/participants/feature-requests/batch",
        method: "POST",
        body: { programParticipantIds }
      })
    }),

    /**
     * AI Assistant Chat - Ask questions about BwC customer data
     * Sends the user's question along with summarized BwC data context to OpenAI
     * Returns AI-generated response based on the data
     */
    askBwCAIAssistant: builder.mutation<IBwCAIChatResponse, IBwCAIChatRequest>({
      query: (request) => ({
        url: "bwc/ai-assistant/chat",
        method: "POST",
        body: request
      })
    })
  })
});

export const {
  useGetBwCCustomersQuery,
  useLazyGetBwCParticipantNotesQuery,
  useLazyGetBwCParticipantFeatureRequestsQuery,
  useGetBwCParticipantNotesBatchMutation,
  useGetBwCParticipantFeatureRequestsBatchMutation,
  useAskBwCAIAssistantMutation
} = buildWithCustomerAPI;
